import { useState } from "react";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverBody,
  Button,
  HStack,
  IconButton,
  Text,
  VStack,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
} from "@chakra-ui/react";
import { Calendar, ChevronDown, ChevronLeft, ChevronRight } from "../../../shared/icons";
import { formatarMesAno, formatarDataCurta, adicionarMeses, parseDataISO } from "../../../shared/lib/date";

function currentMonth() {
  const hoje = new Date();
  return { ano: hoje.getFullYear(), mes: hoje.getMonth() + 1 };
}

function isMesCorrente(period) {
  const atual = currentMonth();
  return period?.ano === atual.ano && period?.mes === atual.mes;
}

function isPersonalizado(period) {
  return Boolean(period?.inicio && period?.fim);
}

function rotulo(period) {
  if (!period) return "Todos os períodos";
  if (isPersonalizado(period)) {
    return `${formatarDataCurta(parseDataISO(period.inicio))} – ${formatarDataCurta(parseDataISO(period.fim))}`;
  }
  return formatarMesAno(period.ano, period.mes);
}

// Filtro de período para Recebíveis e Visão Geral: `period` pode ser `null` (todos os
// períodos), `{ ano, mes }` (mês em que o pagamento cai, navegável — issue #18) ou
// `{ inicio, fim }` (período personalizado pela data trabalhada — datas "yyyy-MM-dd"
// livres, sem alinhamento a mês).
// `mostrarPersonalizado` é só um toggle local de qual seção do popover exibir — a
// navegação mês a mês parte do período já aplicado (ou do mês corrente, se ainda não há
// filtro ou o filtro atual é personalizado) e aplica direto via `onChange`, sem um cursor
// duplicado para sincronizar. Sem filtro, o mês exibido é só o ponto de partida da
// navegação; "Este mês" é o atalho para aplicá-lo com um clique.
export default function PeriodFilterPopover({ period, onChange }) {
  const [mostrarPersonalizado, setMostrarPersonalizado] = useState(isPersonalizado(period));
  const [inicioDraft, setInicioDraft] = useState(period?.inicio ?? "");
  const [fimDraft, setFimDraft] = useState(period?.fim ?? "");
  const displayed = !isPersonalizado(period) && period ? period : currentMonth();

  // A data de início nunca pode ser posterior à de fim — mesma regra do backend
  // (services.IntervaloValido / PeriodoFiltro.validar, que responde 422 num intervalo
  // invertido). Um intervalo de um único dia (início == fim) continua válido. As duas
  // datas vêm de <input type="date"> como "yyyy-MM-dd", formato em que a comparação
  // lexicográfica já é cronológica.
  const intervaloInvertido = Boolean(inicioDraft && fimDraft && fimDraft < inicioDraft);

  const aplicarPersonalizado = () => {
    if (!inicioDraft || !fimDraft || intervaloInvertido) return;
    onChange({ inicio: inicioDraft, fim: fimDraft });
  };

  return (
    <Popover placement="bottom-end">
      <PopoverTrigger>
        <Button size="sm" variant="outline" leftIcon={<Calendar size={14} />} rightIcon={<ChevronDown size={14} />} borderRadius="lg">
          {rotulo(period)}
        </Button>
      </PopoverTrigger>
      <PopoverContent w="260px">
        <PopoverArrow />
        <PopoverBody>
          {mostrarPersonalizado ? (
            <VStack align="stretch" spacing={3}>
              <Text fontSize="xs" color="app-muted-fg">
                Datas em que os plantões foram trabalhados
              </Text>
              <FormControl>
                <FormLabel fontSize="xs" mb={1}>
                  De
                </FormLabel>
                <Input size="sm" type="date" value={inicioDraft} onChange={(e) => setInicioDraft(e.target.value)} />
              </FormControl>
              <FormControl isInvalid={intervaloInvertido}>
                <FormLabel fontSize="xs" mb={1}>
                  Até
                </FormLabel>
                <Input size="sm" type="date" value={fimDraft} onChange={(e) => setFimDraft(e.target.value)} />
                <FormErrorMessage fontSize="xs">A data de início deve ser anterior à data de fim</FormErrorMessage>
              </FormControl>
              <Button size="sm" onClick={aplicarPersonalizado} isDisabled={!inicioDraft || !fimDraft || intervaloInvertido}>
                Aplicar
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setMostrarPersonalizado(false)}>
                Voltar para mês
              </Button>
            </VStack>
          ) : (
            <>
              <Text fontSize="xs" color="app-muted-fg" textAlign="center" mb={1}>
                Mês de pagamento
              </Text>
              <HStack justify="space-between" mb={3}>
                <IconButton
                  aria-label="Mês anterior"
                  icon={<ChevronLeft size={16} />}
                  size="sm"
                  variant="ghost"
                  onClick={() => onChange(adicionarMeses(displayed.ano, displayed.mes, -1))}
                />
                <Text fontSize="sm" fontWeight="semibold">
                  {formatarMesAno(displayed.ano, displayed.mes)}
                </Text>
                <IconButton
                  aria-label="Próximo mês"
                  icon={<ChevronRight size={16} />}
                  size="sm"
                  variant="ghost"
                  onClick={() => onChange(adicionarMeses(displayed.ano, displayed.mes, 1))}
                />
              </HStack>
              <VStack align="stretch" spacing={2}>
                <Button size="sm" variant={isMesCorrente(period) ? "solid" : "outline"} onClick={() => onChange(currentMonth())}>
                  Este mês
                </Button>
                <Button size="sm" variant={!period ? "solid" : "outline"} onClick={() => onChange(null)}>
                  Todos os períodos
                </Button>
                <Button size="sm" variant="outline" onClick={() => setMostrarPersonalizado(true)}>
                  Período personalizado
                </Button>
              </VStack>
            </>
          )}
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}
